/**
 * WatchedCitiesService — user's saved weather locations.
 *
 * localStorage-backed for now; ready to be promoted to /aweni/api/v1/preferences
 * sync later (the public surface is intentionally CRUD + listener so a future
 * server backend can swap in without UI changes).
 */

import type { WatchedCity } from '../modules/home/types';

const LS_KEY = 'aweni_watched_cities';
const MAX_CITIES = 12;

type Listener = (cities: WatchedCity[]) => void;

export class WatchedCitiesService {
  private cities: WatchedCity[] = [];
  private listeners: Listener[] = [];

  constructor() {
    this.loadFromStorage();
  }

  list(): WatchedCity[] {
    return [...this.cities];
  }

  has(lat: number, lng: number): boolean {
    return this.cities.some((c) => sameSpot(c.lat, c.lng, lat, lng));
  }

  add(city: Omit<WatchedCity, 'id'>): WatchedCity | null {
    if (this.cities.length >= MAX_CITIES) return null;
    if (this.has(city.lat, city.lng)) {
      return this.cities.find((c) => sameSpot(c.lat, c.lng, city.lat, city.lng)) || null;
    }
    const entry: WatchedCity = { ...city, id: makeId() };
    this.cities.push(entry);
    this.persist();
    return entry;
  }

  remove(id: string): boolean {
    const before = this.cities.length;
    this.cities = this.cities.filter((c) => c.id !== id);
    if (this.cities.length === before) return false;
    this.persist();
    return true;
  }

  reorder(orderedIds: string[]): void {
    const map = new Map(this.cities.map((c) => [c.id, c]));
    const next: WatchedCity[] = [];
    for (const id of orderedIds) {
      const c = map.get(id);
      if (c) next.push(c);
    }
    // Append any cities not in orderedIds (defensive — shouldn't happen).
    for (const c of this.cities) if (!orderedIds.includes(c.id)) next.push(c);
    this.cities = next;
    this.persist();
  }

  onChange(fn: Listener): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) this.cities = parsed.filter(isWatchedCity);
    } catch {
      /* ignore corrupt storage */
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(this.cities));
    } catch {
      /* quota — silently drop */
    }
    for (const l of this.listeners) {
      try {
        l(this.list());
      } catch {
        /* listener errors must not break persistence */
      }
    }
  }
}

function sameSpot(a: number, b: number, c: number, d: number): boolean {
  // Round to ~1 km — same threshold used by WeatherService's cache key.
  return a.toFixed(2) === c.toFixed(2) && b.toFixed(2) === d.toFixed(2);
}

function makeId(): string {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function isWatchedCity(v: unknown): v is WatchedCity {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.name === 'string' &&
    typeof o.lat === 'number' &&
    typeof o.lng === 'number'
  );
}

let _instance: WatchedCitiesService | null = null;

export function getWatchedCitiesService(): WatchedCitiesService {
  if (!_instance) _instance = new WatchedCitiesService();
  return _instance;
}
